import React, { useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { designTokens } from '@/utils/design-tokens'
import { getMonthRange } from './utils'

interface MonthPickerProps {
  selectedStartDate: Date | null
  /** 월(1일~말일) 선택 완료 */
  onSelect: (startDate: Date, endDate: Date) => void
}

/**
 * 월 단위 전용 피커 (selectionMode='month') — 일 단위 달력 대신
 * 연도별 12개월 그리드에서 한 번의 클릭으로 월을 선택한다.
 */
const MonthPicker: React.FC<MonthPickerProps> = ({ selectedStartDate, onSelect }) => {
  const [year, setYear] = useState(() => (selectedStartDate ?? new Date()).getFullYear())

  const today = new Date()
  const selectedYear = selectedStartDate?.getFullYear()
  const selectedMonth = selectedStartDate?.getMonth()

  const handleMonthClick = (monthIndex: number) => {
    const [start, end] = getMonthRange(new Date(year, monthIndex, 1))
    onSelect(start, end)
  }

  return (
    <div style={{ padding: '16px', width: '300px', maxWidth: '90vw' }}>
      {/* 헤더: 연도 이동 */}
      <div
        className="flex items-center justify-between mb-3"
        style={{ paddingBottom: '12px', borderBottom: `1px solid ${designTokens.colors.gray[1]}` }}
      >
        <button
          onClick={() => setYear(prev => prev - 1)}
          className="flex items-center justify-center w-8 h-8 rounded hover:bg-gray-50 transition-colors"
          style={{ color: designTokens.colors.gray[5] }}
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span
          style={{
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: designTokens.typography.fontSize['body-medium'][0],
            fontWeight: 600,
            color: designTokens.colors.gray[6],
          }}
        >
          {year}년
        </span>
        <button
          onClick={() => setYear(prev => prev + 1)}
          className="flex items-center justify-center w-8 h-8 rounded hover:bg-gray-50 transition-colors"
          style={{ color: designTokens.colors.gray[5] }}
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* 12개월 그리드 */}
      <div className="grid grid-cols-4 gap-1">
        {Array.from({ length: 12 }, (_, monthIndex) => {
          const selected = selectedYear === year && selectedMonth === monthIndex
          const isCurrentMonth = today.getFullYear() === year && today.getMonth() === monthIndex
          return (
            <button
              key={monthIndex}
              onClick={() => handleMonthClick(monthIndex)}
              className="flex items-center justify-center py-2 rounded transition-colors"
              style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: designTokens.typography.fontSize['body-small'][0],
                fontWeight: selected ? 600 : 400,
                backgroundColor: selected ? designTokens.colors.primary.DEFAULT : 'transparent',
                color: selected ? designTokens.colors.white : designTokens.colors.gray[6],
                border: !selected && isCurrentMonth
                  ? `1px solid ${designTokens.colors.primary.DEFAULT}`
                  : '1px solid transparent',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => {
                if (!selected) e.currentTarget.style.backgroundColor = designTokens.colors.gray[0]
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = selected
                  ? designTokens.colors.primary.DEFAULT
                  : 'transparent'
              }}
            >
              {monthIndex + 1}월
            </button>
          )
        })}
      </div>

      {/* 안내 */}
      <div
        className="mt-3 pt-3 text-center"
        style={{
          borderTop: `1px solid ${designTokens.colors.gray[1]}`,
          fontFamily: designTokens.typography.fontFamily.sans[0],
          fontSize: designTokens.typography.fontSize['body-xsmall'][0],
          color: designTokens.colors.gray[4],
        }}
      >
        조회할 월을 선택해주세요
      </div>
    </div>
  )
}

export default MonthPicker
